import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Box,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../api/useFetchObjects";
import axios from "axios";
import { toast } from "react-toastify";

const academicDisplay = (a) => a?.name_display || (a ? `${a.first_name || ""} ${a.last_name || ""}`.trim() || `#${a.id}` : "—");

export default function StageGroupMembersDialog({ open, onClose, group, token, onAdded }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const { data: academicsData, isLoading } = useFetchObjects(
    ["academics-stage-members", search],
    open ? `academics/?page_size=300&status=1${search ? `&search=${encodeURIComponent(search)}` : ""}` : "",
    token,
    { enabled: open }
  );
  const { data: membersData } = useFetchObjects(
    ["stage-group-members", group?.id],
    group?.id && open ? `stage-group-members/?group=${group.id}&page_size=500` : "",
    token,
    { enabled: !!(group?.id && open) }
  );
  const academics = useMemo(() => {
    const raw = academicsData?.results ?? academicsData ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [academicsData]);
  const members = useMemo(() => {
    const raw = membersData?.results ?? membersData ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [membersData]);
  const existingIds = useMemo(() => new Set(members.map((m) => m.academic)), [members]);
  const toggle = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleAll = () => {
    const addable = academics.filter((a) => !existingIds.has(a.id)).map((a) => a.id);
    if (addable.every((id) => selectedIds.includes(id))) setSelectedIds((prev) => prev.filter((id) => !addable.includes(id)));
    else setSelectedIds((prev) => [...new Set([...prev, ...addable])]);
  };

  const handleAddSelected = async () => {
    if (!group?.id || selectedIds.length === 0) return;
    setSaving(true);
    try {
      const base = process.env.REACT_APP_API_URL || "";
      await axios.post(
        `${base}/stage-group-members/bulk-create/`,
        { group: group.id, academics: selectedIds },
        { headers: { Authorization: `Token ${token}` } }
      );
      toast.success(t("academic.membersAdded", "Members added."));
      onAdded?.();
      setSelectedIds([]);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Failed to add members.");
    } finally {
      setSaving(false);
    }
  };

  const addableCount = academics.filter((a) => !existingIds.has(a.id)).length;
  const selectedAddable = selectedIds.filter((id) => !existingIds.has(id)).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("academic.addMembersToGroup", "Add members to group")} {group?.name ? `— ${group.name}` : ""}</DialogTitle>
      <DialogContent>
        <TextField fullWidth size="small" placeholder={t("academic.searchStudent", "Search student")} value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 2 }} />
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}><CircularProgress /></Box>
        ) : (
          <List dense sx={{ maxHeight: 360, overflow: "auto" }}>
            {addableCount > 0 && (
              <ListItem disablePadding>
                <ListItemButton onClick={toggleAll}>
                  <Checkbox edge="start" checked={addableCount > 0 && selectedIds.filter((id) => !existingIds.has(id)).length === addableCount} indeterminate={selectedAddable > 0 && selectedAddable < addableCount} disableRipple />
                  <ListItemText primary={t("academic.selectAll", "Select all")} />
                </ListItemButton>
              </ListItem>
            )}
            {academics.filter((a) => !existingIds.has(a.id)).map((a) => (
              <ListItem key={a.id} disablePadding>
                <ListItemButton onClick={() => toggle(a.id)}>
                  <Checkbox edge="start" checked={selectedIds.includes(a.id)} disableRipple />
                  <ListItemText primary={academicDisplay(a)} />
                </ListItemButton>
              </ListItem>
            ))}
            {academics.length === 0 && <ListItem><ListItemText primary={t("common.noRecords", "No records found.")} /></ListItem>}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel", "Cancel")}</Button>
        <Button variant="contained" onClick={handleAddSelected} disabled={saving || selectedIds.length === 0}>
          {saving ? <CircularProgress size={24} /> : t("academic.addSelected", "Add selected")} {selectedIds.length > 0 ? `(${selectedIds.length})` : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
